import { Box, IconButton, Stack, Tooltip, Typography, TypographyProps } from "@mui/material";
import { GridValidRowModel } from "@mui/x-data-grid-pro";
import React from "react";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";

// https://mui.com/x/react-data-grid/column-definition/#expand-cell-renderer
interface GridCellExpandProps {
  value: string;
  title?: string;
  width: number;
  variant?: TypographyProps["variant"];
  copyButton?: boolean;
}

function isOverflown(element: Element): boolean {
  return (
    element.scrollHeight > element.parentElement!.clientHeight ||
    element.scrollWidth > element.parentElement!.clientWidth
  );
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
  const { width, value, title, variant, copyButton = false } = props;
  const wrapper = React.useRef<HTMLDivElement | null>(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current!);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent: KeyboardEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === "Escape" || nativeEvent.key === "Esc") {
        setShowFullCell(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  const handleCopy = (e) => {
    e.stopPropagation();
    e.preventDefault();
    navigator.clipboard.writeText(value);
  };

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: "center",
        lineHeight: "24px",
        width: "100%",
        height: "100%",
        position: "relative",
        display: "flex",
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: "100%",
          width,
          display: "block",
          position: "absolute",
          top: 0,
        }}
      />
      {title ? (
        <Stack sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
          <Typography fontWeight={500}>{title}</Typography>
          <Tooltip open={showFullCell && showPopper} title={title + " " + value}>
            <Typography
              ref={cellValue}
              color="text.secondary"
              variant={variant}
              sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {value}
            </Typography>
          </Tooltip>
        </Stack>
      ) : (
        <Tooltip open={showFullCell && showPopper} title={value}>
          <Box ref={cellValue} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {value}
          </Box>
        </Tooltip>
      )}
      {copyButton && showFullCell && (
        <Tooltip title={"Copy to clipboard"}>
          <IconButton onClick={handleCopy}>
            <ContentCopyOutlinedIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
});

// function that can be use in datagrid definition => {..., renderCell: renderTableCellStringExpend, ...} : see doc (link at top).
export function renderTableCellStringExpend(params: GridValidRowModel) {
  return <GridCellExpand value={params.value || ""} width={params.colDef.computedWidth} />;
}

export const TableCellStringExpendDescription: React.FC<GridCellExpandProps> = (props) => {
  const { value, title, width, variant } = props;
  return <GridCellExpand value={value} title={title} width={width} variant={variant} />;
};

const TableCellStringExpend: React.FC<GridValidRowModel & { copyButton?: boolean }> = ({
  data,
  copyButton = false,
}) => {
  return <GridCellExpand value={data.value || ""} width={data.colDef.computedWidth} copyButton={copyButton} />;
};

export default TableCellStringExpend;
